import React, { useState } from "react";
import ClientNavbar from "../layout/ClientNavbar";
import { NavLink } from "react-router-dom";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { FiDownload, FiEye, FiTrendingUp, FiTrendingDown, FiRefreshCw, FiX } from "react-icons/fi";

// Format number in Indian Rupees
const formatINR = (amount) =>
  `₹${Number(amount.toFixed(0)).toLocaleString("en-IN")}`;

// Format percentage
const formatPercent = (value) => `${value >= 0 ? "+" : ""}${value.toFixed(2)}%`;

export default function PortfolioOverview() {
  const [timeFilter, setTimeFilter] = useState("1Y"); // 1M, 3M, 6M, 1Y, 3Y, 5Y, ALL
  const [selectedHolding, setSelectedHolding] = useState(null);
  const [showSwitchAlert, setShowSwitchAlert] = useState(false);
  const [switchHolding, setSwitchHolding] = useState(null);
  const [toScheme, setToScheme] = useState("");
  const [switchType, setSwitchType] = useState("Amount");
  const [switchValue, setSwitchValue] = useState("");

  // Static Data - Portfolio Summary
  const portfolioSummary = {
    totalValue: 1250000,
    totalInvested: 1000000,
    totalGains: 250000,
    gainPercent: 25.0,
    xirr: 18.5,
    lastUpdated: "2024-01-15 10:30 AM",
  };

  // Static Data - Holdings
  const holdings = [
    {
      id: 1,
      fundName: "Axis Bluechip Fund - Direct Growth",
      category: "Large Cap",
      amc: "Axis Mutual Fund",
      risk: "Moderate",
      units: 120.45,
      nav: 52.3,
      currentValue: 62995.35,
      investedAmount: 50000,
      gain: 12995.35,
      gainPercent: 25.99,
      xirr: 20.5,
    },
    {
      id: 2,
      fundName: "HDFC Midcap Opportunities - Direct Growth",
      category: "Mid Cap",
      amc: "HDFC Mutual Fund",
      risk: "Moderately High",
      units: 80.2,
      nav: 68.9,
      currentValue: 55257.8,
      investedAmount: 45000,
      gain: 10257.8,
      gainPercent: 22.8,
      xirr: 19.2,
    },
    {
      id: 3,
      fundName: "ICICI Prudential Technology Fund - Direct Growth",
      category: "Sectoral",
      amc: "ICICI Prudential Mutual Fund",
      risk: "High",
      units: 150.75,
      nav: 45.2,
      currentValue: 68139,
      investedAmount: 60000,
      gain: 8139,
      gainPercent: 13.57,
      xirr: 15.8,
    },
    {
      id: 4,
      fundName: "SBI Small Cap Fund - Direct Growth",
      category: "Small Cap",
      amc: "SBI Mutual Fund",
      risk: "Very High",
      units: 200.5,
      nav: 38.5,
      currentValue: 77192.5,
      investedAmount: 70000,
      gain: 7192.5,
      gainPercent: 10.28,
      xirr: 12.5,
    },
    {
      id: 5,
      fundName: "Kotak Equity Hybrid Fund - Direct Growth",
      category: "Hybrid",
      amc: "Kotak Mutual Fund",
      risk: "Moderate",
      units: 95.3,
      nav: 55.8,
      currentValue: 53177.4,
      investedAmount: 50000,
      gain: 3177.4,
      gainPercent: 6.35,
      xirr: 8.2,
    },
    {
      id: 6,
      fundName: "UTI Nifty Index Fund - Direct Growth",
      category: "Index",
      amc: "UTI Mutual Fund",
      risk: "Moderate",
      units: 300.25,
      nav: 42.3,
      currentValue: 127000.58,
      investedAmount: 110000,
      gain: 17000.58,
      gainPercent: 15.46,
      xirr: 16.3,
    },
  ];

  // Static Data - Category Allocation
  const categoryAllocation = [
    { name: "Large Cap", value: 25.4, amount: 317500 },
    { name: "Mid Cap", value: 18.2, amount: 227500 },
    { name: "Small Cap", value: 15.8, amount: 197500 },
    { name: "Sectoral", value: 22.1, amount: 276250 },
    { name: "Hybrid", value: 10.5, amount: 131250 },
    { name: "Index", value: 8.0, amount: 100000 },
  ];

  // Static Data - AMC Allocation
  const amcAllocation = [
    { name: "Axis MF", value: 20.2, amount: 252500 },
    { name: "HDFC MF", value: 18.5, amount: 231250 },
    { name: "ICICI Prudential", value: 22.1, amount: 276250 },
    { name: "SBI MF", value: 15.8, amount: 197500 },
    { name: "Kotak MF", value: 10.5, amount: 131250 },
    { name: "UTI MF", value: 12.9, amount: 161250 },
  ];

  // Static Data - Risk Allocation
  const riskAllocation = [
    { name: "Moderate", value: 35.9, amount: 448750 },
    { name: "Moderately High", value: 18.2, amount: 227500 },
    { name: "High", value: 22.1, amount: 276250 },
    { name: "Very High", value: 15.8, amount: 197500 },
    { name: "Low", value: 8.0, amount: 100000 },
  ];

  // Chart colors
  const COLORS = [
    "#1bafb4",
    "#0f766e",
    "#14b8a6",
    "#2dd4bf",
    "#5eead4",
    "#99f6e4",
  ];

  // Static Data - Target Schemes for Switch
  const targetSchemes = [
    "ICICI Prudential Bluechip Fund - Direct Growth",
    "SBI Flexicap Fund - Direct Growth",
    "Kotak Emerging Equity Fund - Direct Growth",
    "HDFC Balanced Advantage Fund - Direct Growth",
    "Nippon India Large Cap Fund - Direct Growth",
  ];

  const handleExport = () => {
    // Export functionality - can be implemented later
    alert("Export functionality will be implemented");
  };

  const handleViewGains = () => {
    // View gains functionality
    alert("View gains functionality will be implemented");
  };

  const handleOpenHolding = (holding) => {
    setSelectedHolding(holding);
    // Can open a modal or navigate to detail page
    alert(`Opening details for: ${holding.fundName}`);
  };

  const handleSwitchClick = (holding) => {
    setSwitchHolding(holding);
    setToScheme(targetSchemes[0]);
    setSwitchType("Amount");
    setSwitchValue("");
    setShowSwitchAlert(true);
  };

  const handleCloseSwitchAlert = () => {
    setShowSwitchAlert(false);
    setSwitchHolding(null);
    setToScheme("");
    setSwitchValue("");
  };

  const handleSubmitSwitch = () => {
    if (!switchValue || switchValue <= 0) {
      alert("Please enter a valid amount or units");
      return;
    }
    // Handle switch submission
    alert(`Switch order submitted: ${switchHolding.fundName} to ${toScheme}`);
    handleCloseSwitchAlert();
  };

  const estimatedSwitchAmount =
    switchType === "Units" && switchHolding
      ? (switchValue * switchHolding.nav || 0).toFixed(2)
      : switchValue || 0;

  return (
    <>
      <ClientNavbar />
      <div className="page-content" style={{ background: "#f1f5f9" }}>
          {/* Header */}
          <div className="card shadow-sm p-4 mb-4">
            <div className="d-flex justify-content-between align-items-center flex-wrap">
              <div>
                <h3 className="text-primary mb-2">Portfolio Overview</h3>
                <nav
                  aria-label="breadcrumb"
                  style={{ "--bs-breadcrumb-divider": "none" }}
                >
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <NavLink to="/overview">Dashboard</NavLink> /
                    </li>
                    <li className="breadcrumb-item">
                      <NavLink to="/clientmutualfunds">Portfolio</NavLink> /
                    </li>
                    <li className="breadcrumb-item active">Overview</li>
                  </ol>
                </nav>
              </div>
              <div className="d-flex gap-2 align-items-center flex-wrap mt-2">
                <small className="text-muted">
                  Last Updated: {portfolioSummary.lastUpdated}
                </small>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleExport}
                >
                  <FiDownload className="me-1" />
                  Export
                </button>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleViewGains}
                >
                  <FiTrendingUp className="me-1" />
                  View Gains
                </button>
              </div>
            </div>
          </div>

          {/* Portfolio Summary Cards */}
          <div className="row mb-4">
            <div className="col-md-3 col-sm-6 mb-3">
              <div className="card shadow-sm h-100">
                <div className="card-body">
                  <h6 className="text-muted mb-2">Total Portfolio Value</h6>
                  <h4 className="text-primary mb-0">
                    {formatINR(portfolioSummary.totalValue)}
                  </h4>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6 mb-3">
              <div className="card shadow-sm h-100">
                <div className="card-body">
                  <h6 className="text-muted mb-2">Total Invested</h6>
                  <h4 className="mb-0">
                    {formatINR(portfolioSummary.totalInvested)}
                  </h4>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6 mb-3">
              <div className="card shadow-sm h-100">
                <div className="card-body">
                  <h6 className="text-muted mb-2">Total Gains</h6>
                  <h4
                    className={`mb-0 ${portfolioSummary.gainPercent >= 0
                      ? "text-success"
                      : "text-danger"
                      }`}
                  >
                    {formatINR(portfolioSummary.totalGains)} (
                    {formatPercent(portfolioSummary.gainPercent)})
                  </h4>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6 mb-3">
              <div className="card shadow-sm h-100">
                <div className="card-body">
                  <h6 className="text-muted mb-2">XIRR</h6>
                  <h4 className="text-success mb-0">
                    {formatPercent(portfolioSummary.xirr)}
                  </h4>
                </div>
              </div>
            </div>
          </div>

          {/* Time Filter */}
          <div className="card shadow-sm p-3 mb-4">
            <div className="d-flex justify-content-between align-items-center flex-wrap">
              <h6 className="mb-0">Time Period</h6>
              <div className="btn-group" role="group">
                {["1M", "3M", "6M", "1Y", "3Y", "5Y", "ALL"].map((period) => (
                  <button
                    key={period}
                    type="button"
                    className={`btn btn-sm ${timeFilter === period
                      ? "btn-primary"
                      : "btn-outline-primary"
                      }`}
                    onClick={() => setTimeFilter(period)}
                  >
                    {period}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Allocation Charts Row */}
          <div className="row mb-4">
            {/* Category Allocation */}
            <div className="col-lg-4 col-md-6 mb-4">
              <div className="card shadow-sm h-100">
                <div className="card-body">
                  <h5 className="card-title mb-3">Category Allocation</h5>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={categoryAllocation}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) =>
                          `${name}: ${(percent * 100).toFixed(1)}%`
                        }
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {categoryAllocation.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => [
                          `${value}%`,
                          formatINR(
                            categoryAllocation.find((c) => c.value === value)
                              ?.amount || 0
                          ),
                        ]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="mt-3">
                    {categoryAllocation.map((item, index) => (
                      <div
                        key={index}
                        className="d-flex justify-content-between mb-2"
                      >
                        <div className="d-flex align-items-center">
                          <div
                            className="me-2"
                            style={{
                              width: "12px",
                              height: "12px",
                              backgroundColor: COLORS[index % COLORS.length],
                              borderRadius: "2px",
                            }}
                          ></div>
                          <small>{item.name}</small>
                        </div>
                        <small className="fw-semibold">
                          {item.value}% | {formatINR(item.amount)}
                        </small>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* AMC Allocation */}
            <div className="col-lg-4 col-md-6 mb-4">
              <div className="card shadow-sm h-100">
                <div className="card-body">
                  <h5 className="card-title mb-3">AMC Allocation</h5>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={amcAllocation}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) =>
                          `${name}: ${(percent * 100).toFixed(1)}%`
                        }
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {amcAllocation.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => [
                          `${value}%`,
                          formatINR(
                            amcAllocation.find((c) => c.value === value)
                              ?.amount || 0
                          ),
                        ]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="mt-3">
                    {amcAllocation.map((item, index) => (
                      <div
                        key={index}
                        className="d-flex justify-content-between mb-2"
                      >
                        <div className="d-flex align-items-center">
                          <div
                            className="me-2"
                            style={{
                              width: "12px",
                              height: "12px",
                              backgroundColor: COLORS[index % COLORS.length],
                              borderRadius: "2px",
                            }}
                          ></div>
                          <small>{item.name}</small>
                        </div>
                        <small className="fw-semibold">
                          {item.value}% | {formatINR(item.amount)}
                        </small>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Risk Allocation */}
            <div className="col-lg-4 col-md-6 mb-4">
              <div className="card shadow-sm h-100">
                <div className="card-body">
                  <h5 className="card-title mb-3">Risk Allocation</h5>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={riskAllocation}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip
                        formatter={(value) => [
                          `${value}%`,
                          formatINR(
                            riskAllocation.find((r) => r.value === value)
                              ?.amount || 0
                          ),
                        ]}
                      />
                      <Bar dataKey="value" fill="#1bafb4" />
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="mt-3">
                    {riskAllocation.map((item, index) => (
                      <div
                        key={index}
                        className="d-flex justify-content-between mb-2"
                      >
                        <small>{item.name}</small>
                        <small className="fw-semibold">
                          {item.value}% | {formatINR(item.amount)}
                        </small>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Holdings Table */}
          <div className="card shadow-sm mb-4">
            <div className="card-header bg-white d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Holdings</h5>
              <span className="badge bg-primary">
                {holdings.length} Funds
              </span>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Fund Name</th>
                      <th>Category</th>
                      <th>AMC</th>
                      <th>Risk</th>
                      <th className="text-end">Units</th>
                      <th className="text-end">NAV</th>
                      <th className="text-end">Current Value</th>
                      <th className="text-end">Invested</th>
                      <th className="text-end">Gain/Loss</th>
                      <th className="text-end">XIRR</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {holdings.map((holding) => (
                      <tr key={holding.id}>
                        <td>
                          <div>
                            <strong>{holding.fundName}</strong>
                          </div>
                        </td>
                        <td>
                          <span className="badge bg-info">{holding.category}</span>
                        </td>
                        <td>{holding.amc}</td>
                        <td>
                          <span
                            className={`badge ${holding.risk === "Very High"
                              ? "bg-danger"
                              : holding.risk === "High"
                                ? "bg-warning"
                                : holding.risk === "Moderately High"
                                  ? "bg-warning text-dark"
                                  : "bg-success"
                              }`}
                          >
                            {holding.risk}
                          </span>
                        </td>
                        <td className="text-end">{holding.units.toFixed(2)}</td>
                        <td className="text-end">{formatINR(holding.nav)}</td>
                        <td className="text-end">
                          <strong>{formatINR(holding.currentValue)}</strong>
                        </td>
                        <td className="text-end">
                          {formatINR(holding.investedAmount)}
                        </td>
                        <td className="text-end">
                          <span
                            className={
                              holding.gain >= 0 ? "text-success" : "text-danger"
                            }
                          >
                            {holding.gain >= 0 ? (
                              <FiTrendingUp className="me-1" />
                            ) : (
                              <FiTrendingDown className="me-1" />
                            )}
                            {formatINR(holding.gain)} (
                            {formatPercent(holding.gainPercent)})
                          </span>
                        </td>
                        <td className="text-end">
                          <span className="text-success">
                            {formatPercent(holding.xirr)}
                          </span>
                        </td>
                        <td className="text-center">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleOpenHolding(holding)}
                            title="View Details"
                          >
                            <FiEye />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="table-light">
                    <tr>
                      <th colSpan="6" className="text-end">
                        Total
                      </th>
                      <th className="text-end">
                        {formatINR(
                          holdings.reduce(
                            (sum, h) => sum + h.currentValue,
                            0
                          )
                        )}
                      </th>
                      <th className="text-end">
                        {formatINR(
                          holdings.reduce(
                            (sum, h) => sum + h.investedAmount,
                            0
                          )
                        )}
                      </th>
                      <th className="text-end">
                        <span
                          className={
                            portfolioSummary.totalGains >= 0
                              ? "text-success"
                              : "text-danger"
                          }
                        >
                          {formatINR(
                            holdings.reduce((sum, h) => sum + h.gain, 0)
                          )}
                        </span>
                      </th>
                      <th></th>
                      <th></th>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
    </>
  );
}
